// frontend/src/features/regions/RegionsPage.tsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchRegionsSalesData, fetchRegionsClientProducts } from './services/regionsAPI';
import { useFilterStore } from '../../stores/filterStore';
import styles from './RegionsPage.module.css';
import { CarteiraSelector } from '../../components/CarteiraSelector/CarteiraSelector';
import ModernDashboardHeader from '../../components/ModernDashboardHeader/ModernDashboardHeader';
import {
    DateNavigator,
    formatLocalDate,
    getYearRange,
    getWeekRange,
} from '../../components/DateNavigator/DateNavigator';
import navStyles from '../../components/DateNavigator/DateNavigator.module.css';
import { useAuthStore } from '../../stores/authStore';
import RegionsPageSkeleton from './RegionsPageSkeleton';
import KPICard from '../../components/KPICard/KPICard';
import StatesBarChart from './components/StatesBarChart';
import RegionsClientsChart from './components/RegionsClientsChart';
import RegionsInactiveClientsChart from './components/RegionsInactiveClientsChart';
import LineDonutChart from '../../features/dashboard/components/DonutChart/LineDonutChart';
import StateSelector from './components/StateSelector';
import RegionsMap from './components/RegionsMap';
import ProductBarChart from '../../features/products/components/BarChart/ProductBarChart';
import { fetchProductDetails, fetchProductCustomers } from '../../features/products/services/productsAPI';
import ProductDetailModal from '../../features/products/components/Product/ProductDetailModal';
import ClientDetailModal from '../../features/dashboard/components/ClientDetailModal/ClientDetailModal';

const MemoizedStatesBarChart = React.memo(StatesBarChart);
const MemoizedRegionsClientsChart = React.memo(RegionsClientsChart);
const MemoizedRegionsInactiveClientsChart = React.memo(RegionsInactiveClientsChart);
const MemoizedLineDonutChart = React.memo(LineDonutChart);
const MemoizedRegionsMap = React.memo(RegionsMap);
const MemoizedProductBarChart = React.memo(ProductBarChart);

const calculatePercentageChange = (
    current: number,
    previous: number | undefined | null
): number | undefined => {
    if (previous === undefined || previous === null) return undefined;
    if (previous === 0) return current > 0 ? 100.0 : 0.0;
    return ((current - previous) / previous) * 100;
};

const RegionsPage: React.FC = () => {
    const { initialDate, finalDate, selectedCarteiraId, activePreset, setDates } = useFilterStore();
    const [selectedState, setSelectedState] = useState<string | null>(null);
    const [selectedClientFilter, setSelectedClientFilter] = useState<string | null>(null);

    const role = useAuthStore((state) => state.role);
    const isSupervisor = ['supervisor', 'diretor', 'gerente', 'admin'].includes(role || '');

    // State variables for product ranking and modal details
    const [productImages, setProductImages] = useState<Record<string, string>>({});
    const [productNames, setProductNames] = useState<Record<string, string>>({});
    const [selectedProductRef, setSelectedProductRef] = useState<string | null>(null);

    // Map filters
    const [selectedClientMapFilter, setSelectedClientMapFilter] = useState<string | null>(null);
    const [selectedProductMapFilter, setSelectedProductMapFilter] = useState<string | null>(null);
    const [selectedInactiveClient, setSelectedInactiveClient] = useState<any | null>(null);
    const [productFilterClients, setProductFilterClients] = useState<string[]>([]);
    const [selectedLine, setSelectedLine] = useState<string | null>(null);

    // Client details modal state
    const [selectedClientDetails, setSelectedClientDetails] = useState<{ name: string; totalVenda: number; totalMargem: number } | null>(null);
    const [clientProducts, setClientProducts] = useState<any[]>([]);
    const [loadingClientProducts, setLoadingClientProducts] = useState(false);

    const handleProductClick = useCallback((name: string) => {
        setSelectedProductMapFilter(prev => prev === name ? null : name);
        setSelectedClientMapFilter(null);
        setSelectedClientFilter(null);
        setSelectedInactiveClient(null);
        setSelectedLine(null);
    }, []);

    const handleClientClick = useCallback((name: string) => {
        setSelectedClientMapFilter(prev => prev === name ? null : name);
        setSelectedClientFilter(prev => prev === name ? null : name);
        setSelectedProductMapFilter(null);
        setSelectedInactiveClient(null);
        setProductFilterClients([]);
        setSelectedLine(null);
        setSelectedState(null);
    }, []);

    const handleInactiveClientClick = useCallback((client: any) => {
        setSelectedInactiveClient((prev: any) => prev?.name === client.name ? null : client);
        setSelectedClientMapFilter(null);
        setSelectedClientFilter(null);
        setSelectedProductMapFilter(null);
        setProductFilterClients([]);
        setSelectedLine(null);
    }, []);

    const handleLineClick = useCallback((lineName: string) => {
        setSelectedLine(prev => prev === lineName ? null : lineName);
        setSelectedClientMapFilter(null);
        setSelectedClientFilter(null);
        setSelectedProductMapFilter(null);
        setSelectedInactiveClient(null);
        setProductFilterClients([]);
        setSelectedState(null);
    }, []);

    const clearMapFilters = useCallback(() => {
        setSelectedClientMapFilter(null);
        setSelectedClientFilter(null);
        setSelectedProductMapFilter(null);
        setSelectedInactiveClient(null);
        setProductFilterClients([]);
        setSelectedLine(null);
    }, []);

    const handleOpenProductModal = useCallback(() => {
        if (selectedProductMapFilter) {
            setSelectedProductRef(selectedProductMapFilter);
        }
    }, [selectedProductMapFilter]);


    const [periodo, setPeriodo] = useState<'anual' | 'mensal' | 'semanal' | 'custom'>('mensal');
    const [dataRef, setDataRef] = useState<string>(() => formatLocalDate(new Date()));
    const [dataInicio, setDataInicio] = useState<string>('');
    const [dataFim, setDataFim] = useState<string>('');

    // Sincroniza datas com o store
    useEffect(() => {
        if (periodo === 'custom') {
            if (dataInicio && dataFim) setDates(dataInicio, dataFim);
            return;
        }
        const dRef = dataRef || formatLocalDate(new Date());
        let range: { start: Date; end: Date };
        if (periodo === 'mensal') {
            const base = new Date(dRef + 'T12:00:00');
            const year = base.getFullYear();
            const month = base.getMonth();
            if (isSupervisor && !selectedCarteiraId) {
                range = {
                    start: new Date(year, month, 1),
                    end: new Date(year, month + 1, 0),
                };
            } else {
                range = {
                    start: new Date(year, month - 1, 26),
                    end: new Date(year, month, 25),
                };
            }
        } else if (periodo === 'semanal') {
            range = getWeekRange(dRef);
        } else {
            range = getYearRange(dRef);
        }
        setDates(formatLocalDate(range.start), formatLocalDate(range.end));
    }, [periodo, dataRef, dataInicio, dataFim, isSupervisor, selectedCarteiraId, setDates]);

    const queryClient = useQueryClient();
    const [isRefreshing, setIsRefreshing] = useState(false);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            const res = await fetchRegionsSalesData(
                initialDate!,
                finalDate!,
                activePreset || undefined,
                selectedCarteiraId,
                selectedState || undefined,
                selectedClientFilter || undefined,
                selectedLine || undefined,
                true
            );
            const queryKey = ['regionsSalesData', initialDate, finalDate, selectedCarteiraId, selectedState, selectedClientFilter, selectedLine, activePreset];
            queryClient.setQueryData(queryKey, res.data);
        } catch (err) {
            console.error('Erro ao atualizar cache de regiões:', err);
        } finally {
            setIsRefreshing(false);
        }
    };

    // ====== tanstack/react-query Optimization ======
    const { data: apiData, isLoading: loading, error: queryError } = useQuery({
        queryKey: ['regionsSalesData', initialDate, finalDate, selectedCarteiraId, selectedState, selectedClientFilter, selectedLine, activePreset],
        queryFn: async () => {
            const response = await fetchRegionsSalesData(
                initialDate!,
                finalDate!,
                activePreset || undefined,
                selectedCarteiraId,
                selectedState || undefined,
                selectedClientFilter || undefined,
                selectedLine || undefined
            );
            return response.data;
        },
        enabled: !!initialDate && !!finalDate,
        staleTime: 5 * 60 * 1000, // Caches data for 5 minutes (in-memory)
        gcTime: 10 * 60 * 1000,
        refetchOnMount: false,
    });

    const error = queryError ? 'Falha ao carregar dados de vendas por região.' : null;

    const handleOpenClientDetails = useCallback((name: string) => {
        const activeClient = (apiData?.clientsData || []).find((c: any) => c.cliente === name);
        if (activeClient) {
            setSelectedClientDetails({
                name: activeClient.cliente,
                totalVenda: activeClient.totalVenda,
                totalMargem: activeClient.totalMargem
            });
            return;
        }

        const inactiveClient = (apiData?.inactiveClients || []).find((c: any) => c.name === name);
        if (inactiveClient) {
            setSelectedClientDetails({
                name: inactiveClient.name,
                totalVenda: 0,
                totalMargem: 0
            });
            return;
        }

        setSelectedClientDetails({
            name,
            totalVenda: 0,
            totalMargem: 0
        });
    }, [apiData]);

    const handleStateClick = useCallback((estado: string) => {
        setSelectedState((prev) => (prev === estado ? null : estado));
    }, []);

    const formatCurrency = useCallback((value: number) =>
        value.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            notation: 'compact',
            compactDisplay: 'short',
        }), []);

    const formatNumber = useCallback((value: number) => value.toLocaleString('pt-BR'), []);

    const allClientsData = useMemo(() => apiData?.clientsData || [], [apiData]);
    const availableStates = useMemo(() => apiData ? apiData.statesData.map((s) => s.ESTADO) : [], [apiData]);
    const prevKpis = apiData?.previousPeriodKpis;

    const linesConcentrationData = useMemo(() => {
        if (!apiData?.linesData) return [];
        return apiData.linesData.map(l => ({
            name: l.linha,
            value: Number(l.totalVenda) || 0
        }));
    }, [apiData?.linesData]);

    const productsRevenue = useMemo(() => {
        if (!apiData?.productsData) return [];
        return [...apiData.productsData]
            .sort((a, b) => b.totalRevenue - a.totalRevenue)
            .slice(0, 10)
            .map(p => ({
                name: p.name,
                totalRevenue: Number(p.totalRevenue) || 0,
                totalVolume: Number(p.totalVolume) || 0
            }));
    }, [apiData?.productsData]);

    const productsVolume = useMemo(() => {
        if (!apiData?.productsData) return [];
        return [...apiData.productsData]
            .sort((a, b) => b.totalVolume - a.totalVolume)
            .slice(0, 10)
            .map(p => ({
                name: p.name,
                totalRevenue: Number(p.totalRevenue) || 0,
                totalVolume: Number(p.totalVolume) || 0
            }));
    }, [apiData?.productsData]);

    // ── Cross-filter: linha do produto selecionado (para destacar no donut)
    const activeProductLine = useMemo(() => {
        if (!selectedProductMapFilter || !apiData?.productsData) return null;
        const found = apiData.productsData.find((p: any) => p.name === selectedProductMapFilter);
        return (found as any)?.linha || null;
    }, [selectedProductMapFilter, apiData?.productsData]);

    // ── Produtos (filtro por linha é feito via API — backend com ${linhaFilter})
    const productsRevenueFiltered = useMemo(() => {
        if (!apiData?.productsData) return [];
        return [...apiData.productsData]
            .sort((a, b) => b.totalRevenue - a.totalRevenue)
            .slice(0, 10)
            .map((p: any) => ({
                name: p.name,
                totalRevenue: Number(p.totalRevenue) || 0,
                totalVolume: Number(p.totalVolume) || 0
            }));
    }, [apiData?.productsData]);

    // ── Cross-filter: clientes destacados por linha selecionada
    const highlightedClientsByLine = useMemo(() => {
        if (!selectedLine || !allClientsData.length || !apiData?.productsData) return [];
        const clientsInLine = (apiData.clientsData || [])
            .filter((c: any) => c.linha === selectedLine)
            .map((c: any) => c.cliente);
        return clientsInLine;
    }, [selectedLine, allClientsData, apiData]);

    const selectedProduct = useMemo((): any | null => {
        if (!selectedProductRef) return null;
        const found = [...productsRevenue, ...productsVolume]
            .find(p => p.name === selectedProductRef);
        if (!found) return null;
        return {
            Produto: found.name,
            Linha: '',
            Descricao: found.name,
            QuantidadeTotal: found.totalVolume,
            FaturamentoTotal: found.totalRevenue,
            MargemTotal: 0,
            MargemPercentual: 0,
            PrecoMedio: found.totalVolume > 0 ? found.totalRevenue / found.totalVolume : 0,
            NumClientes: 0,
        };
    }, [selectedProductRef, productsRevenue, productsVolume]);

    // Fetch product details (images and description names) for the top models in the region
    useEffect(() => {
        if (!productsRevenue || productsRevenue.length === 0) return;
        let cancelled = false;
        const missing = productsRevenue
            .map(p => p.name)
            .filter(name => !productImages[name]);
        if (!missing.length) return;

        const fetchDetails = async () => {
            const results = await Promise.all(
                missing.map(ref =>
                    fetchProductDetails(ref)
                        .then(res => ({
                            ref,
                            img: (res.data as any).img as string | null,
                            descricao: (res.data as any).DESCRICAO as string | null,
                        }))
                        .catch(() => ({ ref, img: null, descricao: null }))
                )
            );
            if (cancelled) return;
            setProductImages(prev => {
                const next = { ...prev };
                results.forEach(r => { if (r.img) next[r.ref] = r.img; });
                return next;
            });
            setProductNames(prev => {
                const next = { ...prev };
                results.forEach(r => { if (r.descricao) next[r.ref] = r.descricao; });
                return next;
            });
        };
        fetchDetails();
    }, [productsRevenue]);

    // Fetch clients who bought the selected product for map filtering
    useEffect(() => {
        if (!selectedProductMapFilter || !initialDate || !finalDate) {
            setProductFilterClients([]);
            return;
        }
        let cancelled = false;
        fetchProductCustomers(selectedProductMapFilter, { dataI: initialDate, dataF: finalDate })
            .then(res => {
                if (cancelled) return;
                const customersList = ((res.data as any)?.customers || []).map((c: any) => c.NomeCliente);
                setProductFilterClients(customersList);
            })
            .catch(err => {
                console.error("Erro ao buscar clientes do produto:", err);
            });
    }, [selectedProductMapFilter, initialDate, finalDate]);

    // Fetch products purchased by the selected client for detail modal
    useEffect(() => {
        if (!selectedClientDetails || !initialDate || !finalDate) {
            setClientProducts([]);
            return;
        }
        setLoadingClientProducts(true);
        fetchRegionsClientProducts(selectedClientDetails.name, initialDate, finalDate, selectedCarteiraId)
            .then(res => {
                setClientProducts(res.data.clientProducts || []);
            })
            .catch(err => {
                console.error("Erro ao buscar produtos do cliente nas regiões:", err);
            })
            .finally(() => {
                setLoadingClientProducts(false);
            });
    }, [selectedClientDetails, initialDate, finalDate, selectedCarteiraId]);

    const formatDateLabel = (d: string) => {
        if (!d) return '';
        return new Date(d + 'T00:00:00').toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: 'short',
        }).replace(/\./g, '');
    };

    const getPeriodLabel = (): string => {
        if (periodo === 'mensal' && finalDate) {
            const date = new Date(finalDate + 'T00:00:00');
            const month = date.toLocaleDateString('pt-BR', { month: 'long' });
            const year = date.getFullYear().toString().slice(-2);
            return month.charAt(0).toUpperCase() + month.slice(1) + '/' + year;
        }
        if (periodo === 'anual' && initialDate) {
            return new Date(initialDate + 'T00:00:00').getFullYear().toString();
        }
        return `${formatDateLabel(initialDate)} — ${formatDateLabel(finalDate)}`;
    };

    const displayRange = getPeriodLabel();

    const getKpiSubLabel = useCallback((current: number, previous: number | undefined | null) => {
        const change = calculatePercentageChange(current, previous);
        if (change === undefined) return undefined;
        // Evita "+0.0%" — mostra apenas "0.0%"
        if (change === 0) return '0.0%';
        return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
    }, []);



    return (
        <div className={styles.pageContainer}>
            <ModernDashboardHeader 
                selectedCarteira={selectedCarteiraId || ''}
                cacheInfo={{
                    cached: apiData?.cached,
                    cachedAt: apiData?.cachedAt,
                    onRefresh: handleRefresh,
                    isRefreshing: isRefreshing
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <CarteiraSelector />

                    <div className={navStyles.periodContainer}>
                        {(['anual', 'mensal', 'semanal', 'custom'] as const).map((p) => (
                            <button
                                key={p}
                                onClick={() => setPeriodo(p)}
                                className={`${navStyles.periodButton} ${
                                    periodo === p ? navStyles.periodButtonActive : ''
                                }`}
                            >
                                {p === 'custom' ? 'Personalizado' : p}
                            </button>
                        ))}
                    </div>

                    <DateNavigator
                        label=""
                        periodo={periodo}
                        dataRef={dataRef}
                        dataInicio={dataInicio}
                        dataFim={dataFim}
                        displayRange={displayRange}
                        onChange={(vals) => {
                            if (vals.periodo) setPeriodo(vals.periodo);
                            if (vals.dataRef) setDataRef(vals.dataRef);
                            if (vals.dataInicio) setDataInicio(vals.dataInicio);
                            if (vals.dataFim) setDataFim(vals.dataFim);
                        }}
                    />

                    <StateSelector
                        selectedState={selectedState}
                        onStateChange={setSelectedState}
                        availableStates={availableStates}
                    />

                    {/* Active filters moved to widget headers */}
                </div>
            </ModernDashboardHeader>

            {loading ? (
                <RegionsPageSkeleton />
            ) : error ? (
                <p className={styles.errorText}>{error}</p>
            ) : !apiData ? (
                <p className={styles.noDataText}>Nenhum dado encontrado para o período selecionado.</p>
            ) : (
                <>
                    {/* ── KPI Row ── */}
                    <div className={styles.kpiRow}>
                        <KPICard
                            title="Total de Vendas"
                            value={formatCurrency(apiData.kpis.totalVendas)}
                            subLabel1={getKpiSubLabel(apiData.kpis.totalVendas, prevKpis?.totalVendas)}
                        />
                        <KPICard
                            title="Total de Pares"
                            value={formatNumber(apiData.kpis.totalPares)}
                            subLabel1={getKpiSubLabel(apiData.kpis.totalPares, prevKpis?.totalPares)}
                        />
                        <KPICard
                            title="Margem %"
                            value={`${apiData.kpis.margemPercentual.toFixed(2)}%`}
                            subLabel1={getKpiSubLabel(apiData.kpis.margemPercentual, prevKpis?.margemPercentual)}
                        />
                        <KPICard
                            title="Margem Total"
                            value={formatCurrency(apiData.kpis.margemTotal)}
                            subLabel1={getKpiSubLabel(apiData.kpis.margemTotal, prevKpis?.margemTotal)}
                        />
                    </div>

                    {/* ── Charts Grid (Row 1: 3 columns) ── */}
                    <div className={styles.chartsGrid}>
                        {/* Mapa de Distribuição de Clientes */}
                        <div className={styles.chartWidget} style={{ position: 'relative' }}>
                            <h2 className={styles.widgetTitle}>Distribuição Geográfica de Clientes</h2>
                            <div style={{ flex: 1, minHeight: 0 }}>
                                <MemoizedRegionsMap
                                    clients={allClientsData}
                                    selectedState={selectedState}
                                    selectedClientName={selectedClientMapFilter}
                                    highlightedClientNames={
                                        selectedLine
                                            ? highlightedClientsByLine
                                            : productFilterClients
                                    }
                                    inactiveClientToShow={selectedInactiveClient}
                                />
                            </div>

                            {/* Active Filter Indicator floating on the map */}
                            {(selectedClientMapFilter || selectedProductMapFilter || selectedInactiveClient || selectedLine) && (
                                <div style={{
                                    position: 'absolute',
                                    bottom: '16px',
                                    left: '16px',
                                    zIndex: 10,
                                    background: 'rgba(15, 23, 42, 0.9)',
                                    backdropFilter: 'blur(8px)',
                                    border: '1.5px solid rgba(255, 255, 255, 0.15)',
                                    borderRadius: '10px',
                                    padding: '10px 16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    color: '#ffffff',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                                }}>
                                    <span>
                                        Filtro Ativo: {selectedClientMapFilter 
                                            ? `Cliente (${selectedClientMapFilter})` 
                                            : selectedInactiveClient 
                                                ? `Inativo (${selectedInactiveClient.name})` 
                                                : selectedLine
                                                    ? `Linha (${selectedLine})`
                                                    : `Modelo (${selectedProductMapFilter})`}
                                    </span>
                                    {selectedProductMapFilter && (
                                        <button
                                            onClick={handleOpenProductModal}
                                            style={{
                                                background: 'var(--theme-primary, #b91c1c)',
                                                color: '#ffffff',
                                                border: 'none',
                                                borderRadius: '6px',
                                                padding: '4px 12px',
                                                cursor: 'pointer',
                                                fontSize: '11px',
                                                fontWeight: 700,
                                                textTransform: 'uppercase',
                                                transition: 'background-color 0.15s, transform 0.1s',
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#991b1b'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--theme-primary, #b91c1c)'}
                                        >
                                            Detalhes
                                        </button>
                                    )}
                                    {(selectedClientMapFilter || selectedInactiveClient) && (
                                        <button
                                            onClick={() => handleOpenClientDetails(selectedClientMapFilter || selectedInactiveClient.name)}
                                            style={{
                                                background: 'var(--theme-primary, #b91c1c)',
                                                color: '#ffffff',
                                                border: 'none',
                                                borderRadius: '6px',
                                                padding: '4px 12px',
                                                cursor: 'pointer',
                                                fontSize: '11px',
                                                fontWeight: 700,
                                                textTransform: 'uppercase',
                                                transition: 'background-color 0.15s, transform 0.1s',
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#991b1b'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--theme-primary, #b91c1c)'}
                                        >
                                            Detalhes
                                        </button>
                                    )}
                                    <button
                                        onClick={clearMapFilters}
                                        style={{
                                            background: 'rgba(255, 255, 255, 0.1)',
                                            color: '#ffffff',
                                            border: 'none',
                                            borderRadius: '6px',
                                            padding: '4px 12px',
                                            cursor: 'pointer',
                                            fontSize: '11px',
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            transition: 'background-color 0.15s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
                                    >
                                        Limpar
                                    </button>
                                </div>
                            )}
                        </div>
                        {/* Vendas por Estado */}
                        <div className={styles.chartWidget}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                                <h2 className={styles.widgetTitle} style={{ margin: 0 }}>Vendas por Estado</h2>
                                {selectedState && (
                                    <div className={styles.activeFilterPill} style={{ margin: 0, padding: '2px 8px', fontSize: 11 }}>
                                        <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedState}</span>
                                        <button onClick={() => setSelectedState(null)} className={styles.clearFilterBtn} style={{ width: 16, height: 16, fontSize: 11, padding: 0 }}>&times;</button>
                                    </div>
                                )}
                            </div>
                            <div style={{ flex: 1, minHeight: 0 }}>
                                <MemoizedStatesBarChart
                                    data={apiData.statesData || []}
                                    selectedState={selectedState}
                                    onStateClick={handleStateClick}
                                    isSupervisor={isSupervisor}
                                />
                            </div>
                        </div>

                        {/* Participação por Linha */}
                        <div className={styles.chartWidget}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                                <h2 className={styles.widgetTitle} style={{ margin: 0 }}>Participação por Linha</h2>
                                {selectedLine && (
                                    <div className={styles.activeFilterPill} style={{ margin: 0, padding: '2px 8px', fontSize: 11 }}>
                                        <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedLine}</span>
                                        <button onClick={() => handleLineClick(selectedLine)} className={styles.clearFilterBtn} style={{ width: 16, height: 16, fontSize: 11, padding: 0 }}>&times;</button>
                                    </div>
                                )}
                            </div>
                            <div style={{ flex: 1, minHeight: 0 }}>
                                {linesConcentrationData.length > 0 ? (
                                    <MemoizedLineDonutChart
                                        data={linesConcentrationData}
                                        totalLabel="Faturamento"
                                        selectedSegment={selectedLine || activeProductLine}
                                        onSegmentClick={handleLineClick}
                                    />
                                ) : (
                                    <p className={styles.noDataText}>Nenhum dado disponível.</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Row 2 Grid (3 columns) ── */}
                    <div className={styles.chartsGridThree}>
                        {/* Principais Modelos na Região */}
                        <div className={styles.chartWidget}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                                <h2 className={styles.widgetTitle} style={{ margin: 0 }}>Principais Modelos na Região</h2>
                                {selectedProductMapFilter && (
                                    <div className={styles.activeFilterPill} style={{ margin: 0, padding: '2px 8px', fontSize: 11 }}>
                                        <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedProductMapFilter}</span>
                                        <button onClick={() => handleProductClick(selectedProductMapFilter)} className={styles.clearFilterBtn} style={{ width: 16, height: 16, fontSize: 11, padding: 0 }}>&times;</button>
                                    </div>
                                )}
                            </div>
                            <div style={{ flex: 1, minHeight: 0, width: '100%' }}>
                                {productsRevenueFiltered.length > 0 ? (
                                    <MemoizedProductBarChart
                                        byRevenue={productsRevenueFiltered}
                                        byVolume={productsVolume}
                                        activeTab="revenue"
                                        productImages={productImages}
                                        productNames={productNames}
                                        onProductClick={handleProductClick}
                                        selectedProductName={selectedProductMapFilter}
                                    />
                                ) : (
                                    <p className={styles.noDataText}>Nenhum modelo vendido no período.</p>
                                )}
                            </div>
                        </div>

                        {/* Clientes na Região */}
                        <div className={styles.chartWidget}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                                <h2 className={styles.widgetTitle} style={{ margin: 0 }}>Maiores Clientes na Região</h2>
                                {selectedClientFilter && (
                                    <div className={styles.activeFilterPill} style={{ margin: 0, padding: '2px 8px', fontSize: 11 }}>
                                        <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedClientFilter}</span>
                                        <button onClick={() => handleClientClick(selectedClientFilter)} className={styles.clearFilterBtn} style={{ width: 16, height: 16, fontSize: 11, padding: 0 }}>&times;</button>
                                    </div>
                                )}
                            </div>
                            <div style={{ flex: 1, minHeight: 0 }}>
                                {allClientsData.length > 0 ? (
                                    <MemoizedRegionsClientsChart
                                        data={allClientsData}
                                        grandTotalVenda={apiData.kpis.totalVendas}
                                        onClientClick={handleClientClick}
                                        selectedClientName={selectedClientMapFilter}
                                        highlightedClientNames={
                                            selectedLine
                                                ? highlightedClientsByLine
                                                : productFilterClients.length > 0
                                                    ? productFilterClients
                                                    : undefined
                                        }
                                    />
                                ) : (
                                    <p className={styles.noDataText}>Nenhum dado de cliente disponível.</p>
                                )}
                            </div>
                        </div>

                        {/* Maiores Clientes Inativos na Região */}
                        <div className={styles.chartWidget}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                                <h2 className={styles.widgetTitle} style={{ margin: 0 }}>Maiores Clientes Inativos na Região</h2>
                                {selectedInactiveClient && (
                                    <div className={styles.activeFilterPill} style={{ margin: 0, padding: '2px 8px', fontSize: 11 }}>
                                        <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedInactiveClient.name}</span>
                                        <button onClick={() => handleInactiveClientClick(selectedInactiveClient)} className={styles.clearFilterBtn} style={{ width: 16, height: 16, fontSize: 11, padding: 0 }}>&times;</button>
                                    </div>
                                )}
                            </div>
                            <div style={{ flex: 1, minHeight: 0 }}>
                                <MemoizedRegionsInactiveClientsChart
                                    data={apiData.inactiveClients || []}
                                    onClientClick={handleInactiveClientClick}
                                    selectedClientName={selectedInactiveClient?.name}
                                />
                            </div>
                        </div>

                    </div>
                </>
            )}

            {selectedProduct && initialDate && finalDate && (
                <ProductDetailModal
                    product={selectedProduct}
                    startDate={initialDate}
                    endDate={finalDate}
                    onClose={() => setSelectedProductRef(null)}
                />
            )}

            {selectedClientDetails && (
                <ClientDetailModal
                    onClose={() => setSelectedClientDetails(null)}
                    clientName={selectedClientDetails.name}
                    clientProducts={clientProducts}
                    productImages={productImages}
                    productNames={productNames}
                    totalVenda={selectedClientDetails.totalVenda}
                    totalMargem={selectedClientDetails.totalMargem}
                />
            )}
        </div>
    );
};

export default RegionsPage;